import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchLeaderboardSeries,
  fetchLeadtimeSeries,
  selectLeaderboardSeriesData,
  selectLeaderboardSeriesLoading,
  selectLeadtimeSeriesData,
  selectLeadtimeSeriesLoading,
} from '../../../../../slices/reportsSlice';
import AnalyticsHero from '../../../../../components/analytics/AnalyticsHero';
import LeaderboardMatrix from './LeaderboardMatrix';
import ErrorsOverTimeChart from './ErrorsOverTimeChart';
import ErrorByLeadtimeChart from './ErrorByLeadtimeChart';
import { buildColorMap } from './colors';
import { competitorsOnly, withReferenceRanked } from './displayRows';
import { useExcludedDays } from '../../../../../contexts/ExcludedDaysContext';
import { withExcludedBuckets } from '../../../../../utils/excludedDays';

const MATRIX_ROWS = 10;   // leaderboard matrix: how many rank rows to show

// Local-time ISO date (YYYY-MM-DD). Avoids toISOString()'s UTC conversion,
// which in a positive UTC offset pulls the first day of the month back into
// the previous one.
const localIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// The window is the selected month (capped at today for the current month);
// the day view never bleeds into an adjacent month.
function monthRange(year, month) {
  const now = new Date();
  const y = year || now.getFullYear();
  const m = month || now.getMonth() + 1;
  const start = new Date(y, m - 1, 1);
  const isCurrent = y === now.getFullYear() && m === now.getMonth() + 1;
  const end = isCurrent ? now : new Date(y, m, 0);
  return { startDate: localIso(start), endDate: localIso(end) };
}

const horizonLabel = (h) => (h === 0 ? 'Intraday' : `D+${h}`);

// Compact "avg rank" stat for the leaderboard header (best forecaster /
// ensemble). Eyebrow label, the rank value, and a sub-line identifying who.
// When onClick is provided the whole stat becomes a button (spotlight in the
// matrix), with hover affordance and an active highlight.
function RankStat({
  label, value, sub, accent = 'slate', onClick = null, active = false,
  unit = 'avg rank', decimals = 1,
}) {
  const valueColor = accent === 'brand'
    ? 'text-brand-600 dark:text-brand-400'
    : accent === 'gold'
      ? 'text-[#b8860b] dark:text-[#e0b84c]'
      : 'text-slate-800 dark:text-gray-100';
  const content = (
    <>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500 font-mono">
        {label}
      </div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className={`text-xl font-semibold font-mono tabular-nums leading-none ${valueColor}`}>
          {value.toFixed(decimals)}
        </span>
        <span className="text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-wide">
          {unit}
        </span>
      </div>
      <div className="text-[11px] mt-0.5 truncate max-w-[140px] text-slate-500 dark:text-gray-400">
        {sub}
      </div>
    </>
  );
  if (!onClick) {
    return <div className="text-left">{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="Spotlight this forecaster in the leaderboard"
      className={`text-left rounded-md -mx-1.5 px-1.5 py-0.5 transition hover:bg-slate-100/70 dark:hover:bg-gray-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
        active ? 'bg-slate-100 dark:bg-gray-800' : ''
      }`}
    >
      {content}
    </button>
  );
}

/**
 * Analytics → Trends tab content. Controls (track / horizon / forecaster =
 * trace) live in the shared Reports filter bar and are passed in as props; this
 * component fetches and renders the leaderboard matrix (who held each rank,
 * per day). For owners and admins the Predico ensemble is ranked in as an extra
 * square; forecasters never see it (gated server-side).
 */
export default function TrendsReport({
  resourceId,
  track = 'deterministic',
  grain = 'day',
  horizon = 0,
  traceUserId = null,
  onTrace = null,
  isForecaster = false,
  year = null,
  month = null,
  blurSensitive = false,
  ensembleModel = null,
  showOracle = false,
  referenceEnabled = true,
  viewerUserId = null,
}) {
  const dispatch = useDispatch();
  const seriesData = useSelector(selectLeaderboardSeriesData);
  const seriesLoading = useSelector(selectLeaderboardSeriesLoading);
  const leadtimeData = useSelector(selectLeadtimeSeriesData);
  const leadtimeLoading = useSelector(selectLeadtimeSeriesLoading);

  const metricLabel = track === 'probabilistic' ? 'MWI' : 'RMSE';
  const safeHorizon = typeof horizon === 'number' ? horizon : 0;
  const colorMap = React.useMemo(() => buildColorMap(seriesData?.buckets || []), [seriesData]);
  // The selected-forecaster action in either error plot hides that forecaster
  // from both plots. Keep this local to the current Overview slice: changing
  // resource, track, horizon, or period restores the complete field.
  const [hiddenForecasterIds, setHiddenForecasterIds] = useState([]);

  useEffect(() => {
    setHiddenForecasterIds([]);
  }, [resourceId, track, grain, safeHorizon, year, month, ensembleModel]);

  const hideForecaster = useCallback((userId) => {
    if (userId == null) return;
    const id = String(userId);
    setHiddenForecasterIds((current) => (
      current.includes(id) ? current : [...current, id]
    ));
    if (String(traceUserId) === id) onTrace?.(null);
  }, [traceUserId, onTrace]);

  const restoreForecasters = useCallback(() => {
    setHiddenForecasterIds([]);
  }, []);

  // What the matrix and error chart render. Forecasters already get a
  // competitor-only payload from the server; for owners/admins the toggle
  // picks between ranking references into the field (default) and the
  // official competitor-only ranking. Header stats always read the raw
  // payload, so they stay official regardless of the toggle. Excluded
  // (outage) days of this horizon come back as empty, hatched days.
  const excludedDays = useExcludedDays(safeHorizon, year, month);
  const displayData = React.useMemo(() => {
    const view = (isForecaster || !seriesData)
      ? seriesData
      : (referenceEnabled ? withReferenceRanked(seriesData) : competitorsOnly(seriesData));
    return withExcludedBuckets(view, excludedDays);
  }, [seriesData, isForecaster, referenceEnabled, excludedDays]);

  // A contracted forecaster can be spotlighted while references are shown;
  // hide the spotlight when the toggle removes their rows so the view does
  // not dim against a trace that no longer exists.
  const effectiveTraceUserId = React.useMemo(() => {
    if (!traceUserId || referenceEnabled || isForecaster) return traceUserId;
    const isFixed = (seriesData?.buckets || []).some((b) =>
      (b.rows || []).some((r) => r.is_fixed_payment && String(r.user_id) === String(traceUserId)));
    return isFixed ? null : traceUserId;
  }, [traceUserId, referenceEnabled, isForecaster, seriesData]);

  useEffect(() => {
    if (!resourceId) return;
    const { startDate, endDate } = monthRange(year, month);
    dispatch(fetchLeaderboardSeries({
      resourceId, track, grain, startDate, endDate, horizonOffset: safeHorizon,
      ensembleModel,
    }));
  }, [dispatch, resourceId, track, grain, safeHorizon, year, month, ensembleModel]);

  useEffect(() => {
    if (!resourceId || isForecaster) return;
    const { startDate, endDate } = monthRange(year, month);
    dispatch(fetchLeadtimeSeries({
      resourceId, track, startDate, endDate, horizonOffset: safeHorizon,
      ensembleModel,
    }));
  }, [dispatch, resourceId, track, safeHorizon, year, month, ensembleModel, isForecaster]);

  // Period readout (computed server-side over the full daily data): the best
  // forecaster of the month by mean error, with their average rank, and the
  // ensemble's average rank.
  const summary = React.useMemo(() => seriesData?.summary || {}, [seriesData]);
  const best = summary.best_forecaster || null;
  const ensRank = summary.ensemble_avg_rank;

  const summaryRight = (best || ensRank != null) ? (
    <div className="flex items-start gap-5">
      {best && best.avg_rank != null && (
        <RankStat
          label="Best forecaster"
          value={best.avg_rank}
          sub={(!blurSensitive && best.name) ? best.name : (best.username || '?')}
          accent="slate"
          onClick={onTrace && best.user_id
            ? () => onTrace(effectiveTraceUserId === best.user_id ? null : best.user_id)
            : null}
          active={Boolean(best.user_id) && effectiveTraceUserId === best.user_id}
        />
      )}
      {ensRank != null && (
        <RankStat
          label="Ensemble"
          value={ensRank}
          sub="Predico"
          accent="brand"
        />
      )}
    </div>
  ) : null;

  // Error-plot readout: the best forecaster's mean error (the league criterion,
  // straight from the summary) and the ensemble's mean error, derived here from
  // the bucket rows (weighted by scored days, mirroring the backend mean).
  const ensembleAvgError = React.useMemo(() => {
    let sum = 0;
    let days = 0;
    (seriesData?.buckets || []).forEach((b) => {
      (b.rows || []).forEach((r) => {
        if (!r.is_ensemble) return;
        const d = Number(r.n_days) || 1;
        sum += Number(r.avg_score) * d;
        days += d;
      });
    });
    return days ? sum / days : null;
  }, [seriesData]);

  // Oracle mean error: the average across days of each day's lowest forecaster
  // error (the achievable floor of always backing that day's winner). Each day
  // weighs equally — it is already a single best value per day.
  const oracleAvgError = React.useMemo(() => {
    let sum = 0;
    let n = 0;
    (seriesData?.buckets || []).forEach((b) => {
      let min = null;
      (b.rows || []).forEach((r) => {
        if (r.is_ensemble || r.is_fixed_payment) return;
        const v = Number(r.avg_score);
        if (Number.isNaN(v)) return;
        if (min === null || v < min) min = v;
      });
      if (min !== null) { sum += min; n += 1; }
    });
    return n ? sum / n : null;
  }, [seriesData]);

  // The viewing forecaster's own penalty-inclusive mean error, taken straight
  // from the summary so it matches the Errors tab and league exactly (the
  // chart's per-day rows exclude penalty days, so we must not average them
  // here). Lets a forecaster read their error against the day's best.
  const viewerAvgError = React.useMemo(() => {
    if (!viewerUserId) return { value: null, label: null };
    const value = summary.forecaster_avg_errors?.[String(viewerUserId)];
    let label = null;
    (seriesData?.buckets || []).forEach((b) => {
      (b.rows || []).forEach((r) => {
        if (r.is_ensemble || String(r.user_id) !== String(viewerUserId)) return;
        label = (!blurSensitive && r.name) ? r.name : (r.username || null);
      });
    });
    return { value: value != null ? value : null, label };
  }, [seriesData, summary, viewerUserId, blurSensitive]);

  const errorSummaryRight = (
    (isForecaster && viewerAvgError.value != null)
    || (best && best.avg_error != null) || ensembleAvgError != null
    || (showOracle && oracleAvgError != null)
  ) ? (
    <div className="flex items-start gap-5">
      {isForecaster && viewerAvgError.value != null && (
        <RankStat
          label="Your error"
          value={viewerAvgError.value}
          unit="avg error"
          decimals={2}
          sub={viewerAvgError.label || 'You'}
          accent="brand"
          onClick={onTrace && viewerUserId
            ? () => onTrace(effectiveTraceUserId === viewerUserId ? null : viewerUserId)
            : null}
          active={Boolean(viewerUserId) && effectiveTraceUserId === viewerUserId}
        />
      )}
      {best && best.avg_error != null && (
        <RankStat
          label="Best forecaster"
          value={best.avg_error}
          unit="avg error"
          decimals={2}
          sub={(!blurSensitive && best.name) ? best.name : (best.username || '?')}
          accent="slate"
          onClick={onTrace && best.user_id
            ? () => onTrace(effectiveTraceUserId === best.user_id ? null : best.user_id)
            : null}
          active={Boolean(best.user_id) && effectiveTraceUserId === best.user_id}
        />
      )}
      {ensembleAvgError != null && (
        <RankStat
          label="Ensemble"
          value={ensembleAvgError}
          unit="avg error"
          decimals={2}
          sub="Predico"
          accent="brand"
        />
      )}
      {showOracle && oracleAvgError != null && (
        <RankStat
          label="Oracle"
          value={oracleAvgError}
          unit="avg error"
          decimals={2}
          sub="Daily best"
          accent="gold"
        />
      )}
    </div>
  ) : null;
  return (
    <div className="space-y-6 py-4 sm:py-6">
      <AnalyticsHero
        eyebrow="Overview"
        horizonLabel={`${horizonLabel(safeHorizon)} · daily`}
        headline={(
          <>
            Performance{' '}
            <span className="italic font-semibold text-brand-600 dark:text-brand-400">
              over time
            </span>
          </>
        )}
        lede="Daily ranks and errors for forecasters who reached the top 10 on any day. Pick one to spotlight them."
      />

      <section>
        {/* Gap to the card lives on the title block (pb-3), not the header,
            so a taller right-side stat block cannot stretch it. */}
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 pb-3">
            <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
              Leaderboard
            </div>
            <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
              Who held each rank, daily
            </h3>
          </div>
          {summaryRight}
        </header>
        <div className="bg-surface dark:bg-gray-870 rounded-xl shadow-sm border border-gray-200/70 dark:border-gray-600/70 overflow-hidden">
          <div className="p-5">
            {seriesLoading && !(seriesData?.buckets || []).length ? (
              <div className="h-48 rounded-lg border border-gray-200 dark:border-gray-600 animate-pulse" />
            ) : (
              <LeaderboardMatrix
                data={displayData}
                colorMap={colorMap}
                traceUserId={effectiveTraceUserId}
                onTrace={onTrace}
                topN={MATRIX_ROWS}
                metricLabel={metricLabel}
                blurSensitive={blurSensitive}
              />
            )}
            <p className="mt-3 pl-[30px] text-[11px] text-slate-500 dark:text-gray-400">
              {`Each cell is who held that rank that day; hover for the name.${isForecaster ? '' : ' Teal ENS is the Predico ensemble; outlined cells are contracted forecasters.'}`}
            </p>
          </div>
        </div>
      </section>

      <section>
        {seriesLoading && !(seriesData?.buckets || []).length ? (
          <div className="h-72 rounded-xl border border-gray-200 dark:border-gray-600 animate-pulse" />
        ) : (
          <ErrorsOverTimeChart
            data={displayData}
            colorMap={colorMap}
            traceUserId={effectiveTraceUserId}
            onTrace={onTrace}
            topN={MATRIX_ROWS}
            metricLabel={metricLabel}
            blurSensitive={blurSensitive}
            headerRight={errorSummaryRight}
            showOracle={showOracle}
            hiddenUserIds={hiddenForecasterIds}
            onHideUser={hideForecaster}
            onRestoreHidden={restoreForecasters}
          />
        )}
      </section>

      {!isForecaster && (
        leadtimeLoading && !(leadtimeData?.series || []).length ? (
          <div className="h-72 rounded-xl border border-gray-200 dark:border-gray-600 animate-pulse" />
        ) : (
          <ErrorByLeadtimeChart
            data={leadtimeData}
            metricLabel={metricLabel}
            blurSensitive={blurSensitive}
            colorMap={colorMap}
            traceUserId={effectiveTraceUserId}
            onTrace={onTrace}
            hiddenUserIds={hiddenForecasterIds}
            onHideUser={hideForecaster}
            onRestoreHidden={restoreForecasters}
          />
        )
      )}
    </div>
  );
}
